/**
 * Reglas de los respaldos: nombres, cuándo toca respaldar, qué copias se conservan y cuándo avisar.
 * La parte que toca archivos, la base de datos y Google Drive vive en la API (servicios/respaldos.ts).
 */
import { aLocal, sumarDias } from "./agenda.ts";

export type OrigenRespaldo = "programado" | "manual" | "antes_de_restaurar";
export interface Retencion { diarios: number; semanales: number; mensuales: number }
export const RETENCION_POR_DEFECTO: Retencion = { diarios: 7, semanales: 4, mensuales: 12 };
export const EXTENSION_RESPALDO = ".donna";

const DESFASE_MS = 5 * 3600 * 1000; // Ecuador continental, UTC−5

/** donna-2026-09-27-2200-programado.donna (hora de Ecuador) */
export function nombreRespaldo(t: Date, origen: OrigenRespaldo): string {
  const l = aLocal(t);
  return `donna-${l.fecha}-${l.hora.replace(":", "")}-${origen}${EXTENSION_RESPALDO}`;
}

/** Fecha (UTC) de un respaldo a partir de su nombre, o null si el nombre no es de Donna. */
export function fechaDeNombre(nombre: string): Date | null {
  const m = nombre.match(/^donna-(\d{4})-(\d{2})-(\d{2})-(\d{2})(\d{2})-(programado|manual|antes_de_restaurar)\.donna$/);
  if (!m) return null;
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) + DESFASE_MS);
}

export function horaValida(h: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(h);
}

/** Último momento programado (hoy a la hora elegida si ya pasó; si no, ayer). */
export function ultimoProgramado(hora: string, ahora: Date): Date {
  const l = aLocal(ahora);
  const fecha = l.hora >= hora ? l.fecha : sumarDias(l.fecha, -1);
  const [h, m] = hora.split(":").map(Number);
  const [a, mes, d] = fecha.split("-").map(Number);
  return new Date(Date.UTC(a, mes - 1, d, h, m) + DESFASE_MS);
}

/**
 * ¿Toca respaldar? Sí, si desde el último momento programado no hay un respaldo correcto.
 * Así, si la computadora estuvo apagada a la hora del respaldo, se hace apenas se encienda.
 */
export function tocaRespaldo(hora: string, ultimoOk: Date | null, ahora: Date): boolean {
  const u = ultimoProgramado(hora, ahora);
  return !ultimoOk || ultimoOk.getTime() < u.getTime();
}

export function proximoRespaldo(hora: string, ultimoOk: Date | null, ahora: Date): Date {
  if (tocaRespaldo(hora, ultimoOk, ahora)) return ahora;
  return new Date(ultimoProgramado(hora, ahora).getTime() + 24 * 3600 * 1000);
}

/**
 * Rotación abuelo-padre-hijo: se conservan los últimos N diarios, el más reciente de cada una de las
 * últimas N semanas y el más reciente de cada uno de los últimos N meses. Los respaldos hechos antes
 * de una restauración se guardan 30 días. Devuelve los id que se conservan.
 */
export function aConservar(lista: { id: string; fecha: Date; origen?: string }[], r: Retencion, ahora: Date): Set<string> {
  const orden = [...lista].sort((a, b) => b.fecha.getTime() - a.fecha.getTime());
  const guardar = new Set<string>();
  const porDia = new Map<string, string>(), porSemana = new Map<string, string>(), porMes = new Map<string, string>();
  for (const x of orden) {
    if (x.origen === "antes_de_restaurar") {
      if (ahora.getTime() - x.fecha.getTime() <= 30 * 86400000) guardar.add(x.id);
      continue;
    }
    const l = aLocal(x.fecha);
    const d = new Date(l.fecha + "T12:00:00Z");
    const lunes = sumarDias(l.fecha, -((d.getUTCDay() + 6) % 7));
    if (!porDia.has(l.fecha)) porDia.set(l.fecha, x.id);
    if (!porSemana.has(lunes)) porSemana.set(lunes, x.id);
    if (!porMes.has(l.fecha.slice(0, 7))) porMes.set(l.fecha.slice(0, 7), x.id);
  }
  [...porDia.values()].slice(0, r.diarios).forEach((id) => guardar.add(id));
  [...porSemana.values()].slice(0, r.semanales).forEach((id) => guardar.add(id));
  [...porMes.values()].slice(0, r.mensuales).forEach((id) => guardar.add(id));
  return guardar;
}

export function problemasRetencion(r: Partial<Retencion>): string[] {
  const p: string[] = [];
  const ok = (n: unknown, min: number, max: number) => Number.isInteger(n) && (n as number) >= min && (n as number) <= max;
  if (!ok(r.diarios, 1, 60)) p.push("Los respaldos diarios deben ser de 1 a 60.");
  if (!ok(r.semanales, 0, 52)) p.push("Los respaldos semanales deben ser de 0 a 52.");
  if (!ok(r.mensuales, 0, 60)) p.push("Los respaldos mensuales deben ser de 0 a 60.");
  return p;
}

/** La clave cifra los respaldos: sin ella no se pueden restaurar. */
export function problemasClave(clave: string, confirmacion: string): string[] {
  const p: string[] = [];
  if (clave.length < 12) p.push("La clave de respaldo debe tener al menos 12 caracteres.");
  if (/^\d+$/.test(clave)) p.push("La clave no puede ser solo números.");
  if (clave.length >= 12 && new Set(clave).size < 5) p.push("La clave es demasiado repetitiva.");
  if (clave !== confirmacion) p.push("La confirmación no coincide con la clave.");
  return p;
}

/** Aviso para el administrador cuando los respaldos no están al día. */
export function alertaRespaldo(o: { activo: boolean; tieneClave: boolean; ultimoOk: Date | null; fallosSeguidos: number; ahora: Date }): string | null {
  if (!o.tieneClave) return "Los respaldos no están configurados: define la clave de respaldo.";
  if (!o.activo) return "Los respaldos automáticos están desactivados.";
  if (o.fallosSeguidos >= 2) return "Los dos últimos respaldos fallaron. Revisa la pantalla de respaldos.";
  if (!o.ultimoOk) return "Todavía no hay ningún respaldo correcto.";
  const horas = (o.ahora.getTime() - o.ultimoOk.getTime()) / 3600000;
  if (horas > 48) return `El último respaldo correcto fue hace ${Math.floor(horas / 24)} días.`;
  return null;
}

export function tamanoLegible(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}
